/* eslint-disable complexity */
import { useForm, UseFormProps } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
	CreateEventDto,
	defaultEventDuration,
	DiscountTypeEnum,
	EventInstanceDto,
	EventPolicyTypesEnum,
	EventPriceTypesEnum,
	EventPublishingStateEnum,
	getLocalTimeZone,
	getRandomEventCover,
	groupChatAutoGenerateCommand,
	IntakeFormReservedSectionField,
	IntakeFormSection,
	PrivacyTypesEnum,
	ScheduleRepeatTypesEnum,
	shiftScheduleRepeat,
} from '@sweatpals/shared';
import { cloneDeep, compact, isEqual } from 'lodash-es';
import { AttendeeRegistrationFieldTypeEnum, EventTypeEnum, EventWaiverTypeEnum } from '../../../constants';
import { CreateEventFormData, TiersTypeEnum } from '../../../models/dto';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { GetAddressInfoFromAddressCallback } from '../../community-flow';

export const applyTzForDate = (date: Date, dateType: 'UTC' | 'local', timezone: string): Date => {
	if (dateType === 'UTC') {
		return utcToZonedTime(date, timezone);
	} else {
		return zonedTimeToUtc(date, timezone);
	}
};

const resolver = classValidatorResolver(CreateEventFormData);

export const useEventFlowForm = <Context = any>(props?: UseFormProps<CreateEventFormData, Context>) => {
	const form = useForm<CreateEventFormData>({
		...props,
		defaultValues: async () => {
			const defaultTzid = getLocalTimeZone();

			const startDate = applyTzForDate(new Date(), 'local', defaultTzid);
			startDate.setTime(startDate.getTime() + 60 * 60 * 1000); // Add one hour
			startDate.setMinutes(0);
			startDate.setSeconds(0);
			startDate.setTime(startDate.getTime() + 24 * 60 * 60 * 1000); // Add one day

			const endDate = new Date(startDate);
			endDate.setTime(endDate.getTime() + defaultEventDuration);

			return {
				...props?.defaultValues,
				name: '',
				type: EventTypeEnum.Free,
				privacyId: PrivacyTypesEnum.Public,
				tzid: defaultTzid,
				isOnlineEvent: false,
				isRsvpListHidden: false,
				startDate,
				endDate,
				attendeesLimit: null,
				withAttendeesLimit: false,
				policyType: EventPolicyTypesEnum.NoRefunds,
				isCommunityChatSharingEnabled: true,
				fitnessTypes: [],
				allowToPublishToOtherPlatforms: false,

				lastStep: 0,
				eventType: EventTypeEnum.Free,
				withWaiver: false,
				withChatroom: true,

				avatarId: getRandomEventCover(),
			} as CreateEventFormData;
		},
		mode: 'onChange',
		resolver,
	});

	const formData = form.watch();

	return {
		form,
		formData,
		errors: form.formState.errors,
	};
};

export const prepareEventDataBeforeSubmit = async (props: {
	data: CreateEventFormData;
	getAddressInfoFromAddress: GetAddressInfoFromAddressCallback;
	isDraft?: boolean;
	isEditMode?: boolean;
	event?: EventInstanceDto;
	initialData?: CreateEventFormData;
}): Promise<CreateEventDto> => {
	const {
		data,
		event,
		initialData,
		isDraft = false,
		isEditMode = false,
		getAddressInfoFromAddress,
	} = props;

	const eventDto: CreateEventDto = {
		publishingState: isDraft ? EventPublishingStateEnum.Draft : EventPublishingStateEnum.Published,
		draftData: isDraft ? { coHostId: data.cohost?.id } : null,

		// main details
		name: data.name,
		description: data.description,
		subHeaderMarkdown: data.subHeaderMarkdown,
		avatarImg: data.avatarImg?.key ? undefined : data.avatarImg,
		avatarId: data.avatarId,
		authorId: data.authorId,
		coHostId: data.cohost?.id ?? null,
		privacyId: data.privacyId,

		// dates
		startDate: data.startDate ?? undefined,
		endDate: data.endDate ?? undefined,
		tzid: data.tzid,
		scheduleRepeat: data.scheduleRepeat && data.startDate
			? shiftScheduleRepeat(data.startDate, data.scheduleRepeat, false, data.tzid)
			: { typeId: ScheduleRepeatTypesEnum.Never },

		// location
		isOnlineEvent: data.isOnlineEvent,
		onlineUrl: data.onlineUrl,

		// other details
		isRsvpListHidden: data.isRsvpListHidden,
		isWaitlistEnabled: data.isWaitlistEnabled,
		attendeesLimit: data.withAttendeesLimit ? data.attendeesLimit : null,
		eventChatCid: data.withChatroom
			? isEditMode && event?.eventChatCid
				? event.eventChatCid
				: groupChatAutoGenerateCommand
			: null,
		allowToPublishToOtherPlatforms: data.allowToPublishToOtherPlatforms,

		// price details
		isDonationBased: data.type === EventTypeEnum.Donation,
		minimalDonationAmount: data.minimalDonationAmount,
		isPaid: data.type === EventTypeEnum.Paid || data.type === EventTypeEnum.Donation,
		policyType: data.policyType,
		fitnessTypes: data.fitnessTypes,
		additionalData: {
			// copy prev additional data
			...(initialData?.additionalData ?? {}),
		},

		sponsors: data.sponsors,
	};

	if (!isEditMode) {
		eventDto.isCommunityChatSharingEnabled = data.isCommunityChatSharingEnabled;
	}

	if (data.address) {
		const { lat, lng } = await getAddressInfoFromAddress(data.address.description);

		eventDto.addressName = data.address.description;
		eventDto.addressLat = lat.toString();
		eventDto.addressLng = lng.toString();
	}

	if (data.withWaiver && data.waiver) {
		eventDto.waiverUrl = data.waiver.waiverUrl;
		// reset waiver text if waiver type is not text
		eventDto.intakeFormSettings = {
			...(eventDto.intakeFormSettings ?? {}),
			waiverText: data.waiver.waiverText,
		};
	}

	const createdTiers = data.tiers?.filter(t => !t.id) ?? [];

	if (data.tiers?.length || initialData?.tiers?.length) {
		const tierIds = compact((data.tiers ?? []).map(t => t.id));

		const updatedTiers = (data.tiers ?? [])
			.filter(tier => {
				if (!tier.id) {
					return false;
				}

				const oldTier = initialData?.tiers?.find(t => t.id === tier.id);

				return oldTier ? !isEqual(oldTier, tier) : false;
			})
			.map(t => ({ ...t, id: t.id as string }));

		eventDto.deletePricesIds = initialData
			?.tiers
			?.filter(({ id }) => !tierIds.includes(id as string))
			?.map(({ id }) => id) as string[];
		eventDto.updatePrices = updatedTiers.map(({ key, withMemberships, withMaxTickets, eventPricesToMemberships, ...tier }) => ({
			...tier,
			minTicketsPerOrder: tier.tierTypeId === EventPriceTypesEnum.Group ? tier.minTicketsPerOrder : undefined,
			maxTicketsPerOrder: tier.tierTypeId === EventPriceTypesEnum.Group ? tier.maxTicketsPerOrder : undefined,
			maxTickets: withMaxTickets ? tier.maxTickets : null,
			// Convert ticket price to discount for membership
			upsertEventPricesToMemberships: withMemberships ? eventPricesToMemberships?.map(i => ({
				membershipId: i.membershipId,
				type: i.discountType,
				amount:
					i.discountType === DiscountTypeEnum.Fixed
						? Math.max(tier.priceAmount - i.priceAmount, 0)
						: Math.max(100 - i.priceAmount, 0),
			})) : [],
		}));
		eventDto.createPrices = createdTiers.map(({ key, withMemberships, withMaxTickets, eventPricesToMemberships, ...tier }) => ({
			...tier,
			minTicketsPerOrder: tier.tierTypeId === EventPriceTypesEnum.Group ? tier.minTicketsPerOrder : undefined,
			maxTicketsPerOrder: tier.tierTypeId === EventPriceTypesEnum.Group ? tier.maxTicketsPerOrder : undefined,
			maxTickets: withMaxTickets ? tier.maxTickets : null,
			// Convert ticket price to discount for membership
			upsertEventPricesToMemberships: withMemberships ? eventPricesToMemberships?.map(i => ({
				membershipId: i.membershipId,
				type: i.discountType,
				amount:
					i.discountType === DiscountTypeEnum.Fixed
						? Math.max(tier.priceAmount - i.priceAmount, 0)
						: Math.max(100 - i.priceAmount, 0),
			})) : [],
		}));

		if (data.discounts) {
			eventDto.upsertDiscounts = data.discounts.map(({ tiers, withMaxUses, amount, ...discount }) => ({
				...discount,
				maxUses: withMaxUses ? discount.maxUses : null,
				amount: amount ?? 0,
				eventPrices: Object.keys(tiers)
					.map(key => {
						const createdTierIndex = createdTiers.findIndex(t => t.key === key);

						return createdTierIndex === -1 ? key : createdTierIndex;
					})
					.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
			}));
		}
	}

	if (data.attendeeRegistration) {
		const intakeFormSections: IntakeFormSection[] = cloneDeep(event?.intakeFormSettings?.sections ?? []);
		const firstIntakeFormSection = intakeFormSections.shift();

		if (data.attendeeRegistration.fields?.length && data.withQuestionnaire) {
			intakeFormSections.unshift({
				sectionName: firstIntakeFormSection?.sectionName ?? '',
				fields: data.attendeeRegistration.fields.map(({ question, ...field }) => ({
					...field,
					name: question,
					label: field.type !== AttendeeRegistrationFieldTypeEnum.CheckBox ? question : undefined,
					checkboxLabel: field.type === AttendeeRegistrationFieldTypeEnum.CheckBox ? question : undefined,
					tierIds: field.tiersType === TiersTypeEnum.All ? undefined : field.tierIds?.map(tierId => {
						const tierIndex = createdTiers?.findIndex(({ key }) => key === tierId);

						return tierIndex >= 0 ? String(tierIndex) : tierId;
					}),
				}))
			});
		}

		eventDto.intakeFormSettings = {
			...(eventDto.intakeFormSettings ?? {}),
			fullNameReservedField: data.attendeeRegistration.fullNameReservedField as IntakeFormReservedSectionField,
			emailReservedField: data.attendeeRegistration.emailReservedField as IntakeFormReservedSectionField,
			phoneReservedField: data.attendeeRegistration.phoneReservedField as IntakeFormReservedSectionField,
			sections: intakeFormSections,
			showSectionsOnceForMultipleTickets: data.attendeeRegistration.showSectionsOnceForMultipleTickets,
		};
	}

	if (eventDto.additionalData) {
		eventDto.additionalData.ticketSalesStartUtcTimeIso = data.withSalesStartDate && data.salesStartDate instanceof Date
			? data.salesStartDate.toISOString() : undefined;
	}

	return eventDto;
};
